import { useState, useEffect, useMemo, useRef } from "react";
import Dialog from "@mui/material/Dialog";
import Box from "@mui/material/Box";
import Slider from "@mui/material/Slider";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { useRouter } from "next/router";
import { api } from "@/utils/Axios";
import { arFont, enFont } from "@/styles/font";
import useDebounce from "@/hooks/useDebounce";
import en from "../../../locales/common/en";
import ar from "../../../locales/common/ar";
import { calcAr_schema } from "../calcAr_schema";
import { calcEn_schema } from "../calcEn_schema";
import SmallTitle from "../mini-components/SmallTitle";
import ProgressHeader from "../circle-progress";
import AmountCard from "../amount-card";
import InputField from "../input-field";
import CustomText from "../text";
import { formatYearsLabel } from "@/utils/general-functions";

export default function CalcPopup({
  open,
  onClose,
  data,
  max_amount,
  price_label,
  noDownpayment,
  price_desc,
}) {
  const [productPrice, setProductPrice] = useState("");
  const debouncedValue = useDebounce(productPrice, 500);

  // downPayment for api
  const [downPayment, setDownPayment] = useState(0);
  const [downPaymentPercent, setDownPaymentPercent] = useState(0);

  // downPayment price input
  const [downPaymentInput, setDownPaymentInput] = useState("");
  const [downPaymentInputError, setDownPaymentInputError] = useState("");

  const [duration, setDuration] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);

  const [value, setValue] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [isPriceFocused, setIsPriceFocused] = useState(false);
  const [isDownFocused, setIsDownFocused] = useState(false);
  const [retryAfterSeconds, setRetryAfterSeconds] = useState(0);

  const prevPriceRef = useRef("");
  const inFlightRef = useRef(false);
  const cooldownUntilRef = useRef(0);

  const hasSinglePlan = data?.length === 1;
  const singlePlan = hasSinglePlan ? data[0] : null;

  const router = useRouter();
  const { locale } = router;
  const t = locale === "en" ? en : ar;

  const validateInput = locale === "ar" ? calcAr_schema : calcEn_schema;

  const theme = useMemo(
    () => createTheme({ direction: locale === "ar" ? "rtl" : "ltr" }),
    [locale],
  );

  const minAmount = 1000;

  const formatNumber = (val) => {
    if (val === undefined || val === null || val === "" || isNaN(val))
      return "";
    const num = Number(String(val).replace(/,/g, ""));
    if (isNaN(num)) return "";
    const localeFormat = locale === "ar" ? "ar-EG" : "en-EG";
    return num.toLocaleString(localeFormat);
  };

  const parseNumber = (val) => Number(String(val ?? "").replace(/,/g, "")) || 0;

  // percent <-> amount
  const clamp = (v, min, max) => Math.min(Math.max(v, min), max);

  const percentToAmount = (price, pct) => {
    const p = Number(pct) || 0;
    const priceNum = parseNumber(price);
    if (!priceNum) return 0;
    return Math.round(priceNum * (p / 100));
  };

  const amountToPercent = (price, amount) => {
    const priceNum = parseNumber(price);
    const a = Number(amount) || 0;
    if (!priceNum) return 0;
    return Math.round((a / priceNum) * 100);
  };

  const getRateLimitMessage = (seconds) =>
    locale === "en"
      ? seconds > 0
        ? `Too many requests. Please try again after ${seconds} seconds.`
        : "Too many requests. Please wait 1 minute before trying again."
      : seconds > 0
        ? `طلبات كثيرة جدًا. برجاء المحاولة بعد ${seconds} ثانية.`
        : "تم تجاوز عدد المحاولات المسموح. برجاء الانتظار دقيقة قبل المحاولة مرة أخرى.";

  const isRateLimited = retryAfterSeconds > 0;

  // أقل نسبة من الباكدج المختارة
  const minDownPaymentPercent = useMemo(() => {
    if (!selectedPlan || selectedPlan.downPayment === 0) return 0;
    return Number(selectedPlan.downPayment ?? 0);
  }, [selectedPlan]);

  const priceNum = useMemo(() => parseNumber(productPrice), [productPrice]);

  // min/max للمبلغ بناء على النسبة (min%) و (100%)
  const minDownPaymentAmount = useMemo(() => {
    if (!selectedPlan || !priceNum) return 0;
    if (selectedPlan.allowDownPayment === 0) return 0;
    return Math.round(priceNum * (minDownPaymentPercent / 100));
  }, [selectedPlan, priceNum, minDownPaymentPercent]);

  const maxDownPaymentAmount = useMemo(() => {
    if (!priceNum) return 0;
    return Math.round(priceNum * 1);
  }, [priceNum]);

  // countdown for 429 cooldown
  useEffect(() => {
    if (retryAfterSeconds <= 0) return;

    const timer = setInterval(() => {
      const left = Math.max(
        0,
        Math.ceil((cooldownUntilRef.current - Date.now()) / 1000),
      );

      setRetryAfterSeconds(left);

      if (left === 0) {
        setErrorMessage("");
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [retryAfterSeconds]);

  // price input
  const handleProductPriceChange = (e) => {
    const re = /^[0-9\b]+$/;
    const val = e.target.value;

    if (val === "" || re.test(val)) {
      const prev = prevPriceRef.current;
      const isRealChange = prev !== "" && val !== prev;

      setProductPrice(val);
      prevPriceRef.current = val;

      if (val === "") {
        setDownPayment(0);
        setDownPaymentPercent(0);
        setDownPaymentInput("");
        setDownPaymentInputError("");
        setValue(null);
        setDuration(null);
        setErrorMessage("");
        return;
      }

      if (isRealChange) {
        setDownPayment(0);
        setDownPaymentPercent(0);
        setDownPaymentInput("");
        setDownPaymentInputError("");
        setValue(null);
      }
    }
  };

  // api calc
  const calculateInstallment = async (price, plan, months, down) => {
    const now = Date.now();

    // لو في cooldown شغال امنع أي request جديد
    if (now < cooldownUntilRef.current) {
      const left = Math.ceil((cooldownUntilRef.current - now) / 1000);
      setRetryAfterSeconds(left);
      setErrorMessage(getRateLimitMessage(left));
      return;
    }

    // امنع request جديد لو في واحد شغال بالفعل
    if (inFlightRef.current) return;

    inFlightRef.current = true;
    setLoading(true);

    try {
      const res = await api.post("/services/general-loan-calculator", {
        amount: +price,
        downPayment: down,
        packageId: plan.packageId,
        tenors: months,
      });

      setValue(res?.data?.amount);
      setErrorMessage("");
    } catch (error) {
      const status = error?.response?.status;

      if (status === 429) {
        cooldownUntilRef.current = Date.now() + 60 * 1000;
        setRetryAfterSeconds(60);
        setValue(null);
        setErrorMessage(getRateLimitMessage(0));
      } else {
        setValue(null);
      }
    } finally {
      inFlightRef.current = false;
      setLoading(false);
    }
  };

  const checkInputs = (price, plan, months, down) => {
    const priceN = parseNumber(price);
    const monthsNum = Number(months) || 0;
    const downNum = Number(down) || 0;

    if (Date.now() < cooldownUntilRef.current) {
      const left = Math.ceil((cooldownUntilRef.current - Date.now()) / 1000);
      setRetryAfterSeconds(left);
      setErrorMessage(getRateLimitMessage(left));
      return;
    }

    if (priceN && plan && monthsNum && downNum >= 0) {
      const validation = validateInput(priceN, minAmount, max_amount);
      if (validation.valid) {
        calculateInstallment(priceN, plan, monthsNum, downNum);
      } else {
        setErrorMessage(validation.message);
        setValue(null);
      }
    } else {
      setValue(null);
    }
  };

  // plan change
  const handlePlanChange = (e) => {
    const selected = data.find((p) => p.packageId === e.target.value);
    if (!selected) return;

    setSelectedPlan(selected);

    if (priceNum > 0) {
      const minPct = Number(selected.downPayment || 0);
      const pct = clamp(minPct, minPct, 100);

      setDownPaymentPercent(pct);

      const dpAmount = Math.min(percentToAmount(priceNum, pct), priceNum);
      setDownPayment(dpAmount);

      if (isDownFocused) setDownPaymentInput(String(dpAmount));

      setDownPaymentInputError("");

      if (duration) checkInputs(priceNum, selected, duration, dpAmount);
    }
  };

  // duration change
  const handleDurationChange = (e) => {
    const months = Number(e.target.value) || 0;
    setDuration(months);

    if (productPrice && selectedPlan) {
      checkInputs(productPrice, selectedPlan, months, downPayment);
    }
  };

  // input downPayment field change
  const handleDownPaymentInputChange = (e) => {
    const re = /^[0-9\b]+$/;
    const raw = e.target.value;

    if (raw === "") {
      setDownPaymentInput("");
      setDownPaymentInputError("");
      return;
    }

    if (!re.test(raw)) return;

    setDownPaymentInput(raw);

    const val = parseNumber(raw);

    const minA = minDownPaymentAmount;
    const maxA = maxDownPaymentAmount;

    if (!priceNum || !selectedPlan) {
      setDownPaymentInputError("");
      return;
    }

    if (selectedPlan.allowDownPayment === 0) {
      setDownPaymentInputError("");
      setDownPayment(0);
      setDownPaymentPercent(0);
      return;
    }

    if (val < minA || val > maxA) {
      setDownPaymentInputError(
        t.calc.downPaymentRange
          .replace("{{min}}", formatNumber(minA))
          .replace("{{max}}", formatNumber(maxA)),
      );

      return;
    }

    setDownPaymentInputError("");
    setDownPayment(val);

    const pct = clamp(
      amountToPercent(priceNum, val),
      minDownPaymentPercent,
      100,
    );
    setDownPaymentPercent(pct);

    if (priceNum && selectedPlan && duration) {
      checkInputs(priceNum, selectedPlan, duration, val);
    }
  };

  // slider percent handlers
  const handleDownPaymentSliderChange = (e, newPct) => {
    const pct = clamp(Number(newPct) || 0, minDownPaymentPercent, 100);

    setDownPaymentPercent(pct);

    const dpAmount = Math.min(percentToAmount(priceNum, pct), priceNum);
    setDownPayment(dpAmount);

    if (isDownFocused) setDownPaymentInput(String(dpAmount));
    setDownPaymentInputError("");
  };

  const handleDownPaymentRequest = (e, newPct) => {
    if (downPaymentInputError) return;

    const pct = clamp(Number(newPct) || 0, minDownPaymentPercent, 100);
    const dpAmount = Math.min(percentToAmount(priceNum, pct), priceNum);

    checkInputs(productPrice, selectedPlan, duration, dpAmount);
  };

  // لما السعر + الخطة موجودين: خلي downPaymentPercent يبدأ من min% (لو لسه 0)
  useEffect(() => {
    if (!priceNum || !selectedPlan) return;

    if (selectedPlan.allowDownPayment === 0) {
      setDownPaymentPercent(0);
      setDownPayment(0);
      setDownPaymentInputError("");
      return;
    }

    setDownPaymentPercent((prev) => {
      const base = prev && prev > 0 ? prev : minDownPaymentPercent;
      const safePct = clamp(base, minDownPaymentPercent, 100);

      const dpAmount = Math.min(percentToAmount(priceNum, safePct), priceNum);
      setDownPayment(dpAmount);
      setDownPaymentInputError("");

      return safePct;
    });
  }, [priceNum, selectedPlan, minDownPaymentPercent]);

  // debounce compute
  useEffect(() => {
    if (
      debouncedValue &&
      selectedPlan &&
      duration &&
      !downPaymentInputError &&
      !isRateLimited
    ) {
      checkInputs(debouncedValue, selectedPlan, duration, downPayment);
    }
  }, [debouncedValue, selectedPlan, duration, isRateLimited]); // downPayment متساب لتجنب spam

  useEffect(() => {
    if (debouncedValue !== "") {
      const validation = validateInput(
        parseNumber(debouncedValue),
        minAmount,
        max_amount,
      );
      if (!validation.valid) {
        setErrorMessage(validation.message);
        setDownPayment(0);
        setDownPaymentPercent(0);
        setDownPaymentInput("");
        setDownPaymentInputError("");
      } else if (!isRateLimited) {
        setErrorMessage("");
      }
    }
  }, [debouncedValue, isRateLimited]);

  const getTenorOptions = () => {
    if (!selectedPlan) return [];
    const options = [];
    if (selectedPlan.minTenor % 12 !== 0) options.push(selectedPlan.minTenor);
    const start = Math.ceil(selectedPlan.minTenor / 12) * 12;
    for (let m = start; m <= selectedPlan.maxTenor; m += 12) options.push(m);
    return options;
  };

  const getTenorLabel = (m) => {
    if (m < 12) {
      return `${formatNumber(m)} ${t.calc.months}`;
    }

    const years = m / 12;
    return formatYearsLabel(years, locale);
  };

  // progress calc
  const tenorOptions = useMemo(() => getTenorOptions(), [selectedPlan]);
  const maxTenor = useMemo(
    () => (tenorOptions.length ? Math.max(...tenorOptions) : 0),
    [tenorOptions],
  );

  const progress = useMemo(() => {
    const dur = Number(duration || 0);
    if (!maxTenor || !dur) return 0;
    const safeDur = Math.min(dur, maxTenor);
    return Math.round((safeDur / maxTenor) * 100);
  }, [duration, maxTenor]);

  useEffect(() => {
    if (!open) {
      setProductPrice("");
      setDownPayment(0);
      setDownPaymentPercent(0);
      setDownPaymentInput("");
      setDownPaymentInputError("");
      setDuration(null);
      setSelectedPlan(null);
      setValue(null);
      setErrorMessage("");
      setIsPriceFocused(false);
      setIsDownFocused(false);
      setRetryAfterSeconds(0);
      cooldownUntilRef.current = 0;
      inFlightRef.current = false;
      prevPriceRef.current = "";
    }
  }, [open]);

  // auto single plan
  useEffect(() => {
    if (!hasSinglePlan || !singlePlan) return;
    if (selectedPlan?.packageId === singlePlan.packageId) return;

    setSelectedPlan(singlePlan);

    if (priceNum > 0) {
      const minPct = Number(singlePlan.downPayment || 0);
      const pct = clamp(minPct, minPct, 100);

      setDownPaymentPercent(pct);

      const dpAmount = Math.min(percentToAmount(priceNum, pct), priceNum);
      setDownPayment(dpAmount);

      if (isDownFocused) setDownPaymentInput(String(dpAmount));
      setDownPaymentInputError("");

      if (duration) checkInputs(priceNum, singlePlan, duration, dpAmount);
    }
  }, [
    hasSinglePlan,
    singlePlan,
    selectedPlan?.packageId,
    priceNum,
    duration,
    isDownFocused,
  ]);

  // gradient fill percent (0..100)
  const sliderFill = useMemo(() => {
    const minPct = minDownPaymentPercent || 0;
    const valPct = clamp(downPaymentPercent, minPct, 100);
    if (100 === minPct) return 100;
    return ((valPct - minPct) / (100 - minPct)) * 100;
  }, [downPaymentPercent, minDownPaymentPercent]);

  const minMark = useMemo(
    () => clamp(minDownPaymentPercent || 0, 0, 100),
    [minDownPaymentPercent],
  );

  const activeFill = useMemo(() => {
    const val = clamp(downPaymentPercent, minMark, 100);
    if (100 === minMark) return 100;
    return ((val - minMark) / (100 - minMark)) * 100;
  }, [downPaymentPercent, minMark]);
  useEffect(() => {
    if (!isDownFocused) {
      setDownPaymentInput(String(downPayment || ""));
    }
  }, [downPayment, isDownFocused]);
  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: "16px",
          backgroundColor: "#F8FAFC",
          boxShadow: "0 8px 30px rgba(0,0,0,0.25)",
          maxWidth: "none",
          p: { xs: 1, md: 3 },
          width: { xs: "100%", sm: "80%", lg: "55%" },
          margin: { xs: "16px", md: "auto" },
        },
      }}
    >
      <Box
        className={locale === "en" ? enFont.className : arFont.className}
        sx={{
          direction: locale === "ar" ? "rtl" : "ltr",
          display: "flex",
          flexWrap: { xs: "wrap", md: "nowrap" },
          gap: 2,
          px: "2px",
          overflow: { xs: "scroll", md: "unset" },
        }}
      >
        {/* data enter */}
        <Box
          sx={{
            boxShadow:
              "0 10px 15px -3px rgba(0, 0, 0, 0.10), 0 4px 6px -4px rgba(0, 0, 0, 0.10);",
            borderRadius: "16px",
            padding: { xs: "20px", md: "24px 30px" },
            width: { xs: "100%", md: "60%" },
            bgcolor: "#fff",
          }}
        >
          <SmallTitle
            title={t.calcTitle}
            customeStyle={{ xs: "20px", sm: "24px" }}
          />

          {price_desc && (
            <CustomText
              text={price_desc}
              customStyle={{ fontSize: "12px", color: "#000", mt: 3 }}
            />
          )}

          {/* {isRateLimited && (
            <CustomText
              text={getRateLimitMessage(retryAfterSeconds)}
              customStyle={{
                fontSize: "12px",
                color: "#D32F2F",
                mt: 1.5,
                mb: 1,
              }}
            />
          )} */}

          <Box
            sx={{
              display: "flex",
              gap: { xs: 1, sm: 2 },
              flexWrap: "wrap",
              mt: 2,
            }}
          >
            {/* price */}
            <Box sx={{ flex: 1, width: { xs: "100%", md: "50%" } }}>
              <InputField
                type="text"
                label={price_label}
                value={productPrice}
                handleChange={(val) =>
                  handleProductPriceChange({ target: { value: val } })
                }
                error={errorMessage}
                shrink={true}
                disabled={isRateLimited}
                endIcon={<span>{t.calc.egyptianPound}</span>}
              />
            </Box>

            {/* package */}
            {!hasSinglePlan && (
              <Box sx={{ flex: 1, width: { xs: "100%", md: "50%" } }}>
                <InputField
                  type="selectbox"
                  label={t.purpose2}
                  placeholder={t.purpose2}
                  value={selectedPlan?.packageId || ""}
                  valuesOption={data || []}
                  getOptionValue={(o) => o.packageId}
                  getOptionLabel={(o) => o.title}
                  handleChange={(val) =>
                    handlePlanChange({ target: { value: val } })
                  }
                  disabled={isRateLimited}
                />
              </Box>
            )}
          </Box>

          {/* duration */}
          <Box sx={{ flex: 1, width: { xs: "100%" } }}>
            <InputField
              type="selectbox"
              label={t.calc.duration}
              value={duration || ""}
              disabled={!selectedPlan || isRateLimited}
              valuesOption={tenorOptions}
              getOptionValue={(opt) => opt}
              getOptionLabel={(opt) => getTenorLabel(opt)}
              handleChange={(val) =>
                handleDurationChange({ target: { value: val } })
              }
            />
          </Box>

          {/* downpayment */}
          {!noDownpayment && (
            <>
              <Box sx={{ bgcolor: "white", pt: 2 }}>
                <Box
                  sx={{
                    width: { xs: "60%", md: "40%" },
                    mb: 0.5,
                    mr: locale === "ar" && "auto",
                    ml: locale === "en" && "auto",
                  }}
                >
                  <InputField
                    type="text"
                    label={t.calc.preAmount}
                    value={
                      isDownFocused
                        ? downPaymentInput
                        : formatNumber(downPayment)
                    }
                    handleChange={(val) =>
                      handleDownPaymentInputChange({ target: { value: val } })
                    }
                    onFocus={() => {
                      setIsDownFocused(true);
                      setDownPaymentInputError("");
                    }}
                    onBlur={() => {
                      setIsDownFocused(false);
                    }}
                    error={downPaymentInputError}
                    disabled={
                      !productPrice ||
                      !selectedPlan ||
                      !!errorMessage ||
                      selectedPlan?.allowDownPayment === 0 ||
                      isRateLimited
                    }
                    shrink={true}
                    endIcon={<span>{t.calc.egyptianPound}</span>}
                  />
                </Box>

                <ThemeProvider theme={theme}>
                  <Box
                    sx={{
                      overflow: "visible",
                      mb: { xs: 3, md: 1 },
                      position: "relative",
                    }}
                  >
                    <Slider
                      value={clamp(
                        downPaymentPercent,
                        minDownPaymentPercent,
                        100,
                      )}
                      min={0}
                      max={100}
                      step={1}
                      onChange={handleDownPaymentSliderChange}
                      onChangeCommitted={handleDownPaymentRequest}
                      valueLabelDisplay="on"
                      valueLabelFormat={(v) => `${v}%`}
                      disabled={
                        !productPrice ||
                        !selectedPlan ||
                        !!errorMessage ||
                        selectedPlan?.allowDownPayment === 0 ||
                        isRateLimited
                      }
                      sx={{
                        direction: locale === "ar" ? "rtl" : "ltr",
                        mt: 1,
                        height: "8px",
                        borderRadius: "12px",
                        "& .MuiSlider-track": { display: "none" },
                        "& .MuiSlider-rail": {
                          opacity: 1,
                          borderRadius: "12px",
                          background: `linear-gradient(${
                            locale === "ar" ? "to left" : "to right"
                          },
                        #FF7701 0%,
                        #FF7701 ${minMark}%,
                        #FF7701 ${minMark + (activeFill * (100 - minMark)) / 100}%,
                        #EAECF0 ${minMark + (activeFill * (100 - minMark)) / 100}%,
                        #EAECF0 100%
                      )`,
                        },
                        "& .MuiSlider-thumb": {
                          width: 18,
                          height: 18,
                          backgroundColor: "#FF7701",
                          boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                          "&:focus, &:hover, &.Mui-active": {
                            boxShadow: "0 0 0 8px rgba(255,119,1,0.15)",
                          },
                          transform:
                            locale === "ar"
                              ? "translateX(6px) translateY(-50%)"
                              : "translateX(0px) translateY(-50%)",
                          top: "50%",
                        },
                        "& .MuiSlider-valueLabel": {
                          top: "25px",
                          background: "transparent",
                          transform: "translateX(-40%)",
                        },
                        "& .MuiSlider-valueLabelLabel": {
                          background: "#FF7701",
                          color: "#fff",
                          padding: "6px 12px",
                          borderRadius: "10px",
                          fontSize: { xs: "10px", md: "13px" },
                          whiteSpace: "nowrap",
                          boxShadow: "0 8px 20px rgba(0,0,0,.15)",
                          position: "absolute",
                          right: "-20px",
                          top: "10px",
                        },
                        "& .MuiSlider-valueLabelLabel::before": {
                          content: '""',
                          position: "absolute",
                          top: "-3px",
                          left: "50%",
                          width: "10px",
                          height: "10px",
                          background: "#FF7701",
                          transform: "translateX(-50%) rotate(45deg)",
                        },
                      }}
                    />
                    {/* 
                    <Box
                      component="span"
                      sx={{
                        position: "absolute",
                        bottom: -4,
                        left: locale === "ar" ? "auto" : 0,
                        right: locale === "ar" ? 0 : "auto",
                        fontSize: "12px",
                        color: "#545A62",
                      }}
                    >
                      %0
                    </Box>

                    <Box
                      component="span"
                      sx={{
                        position: "absolute",
                        bottom: -4,
                        right: locale === "ar" ? "auto" : 0,
                        left: locale === "ar" ? 0 : "auto",
                        fontSize: "12px",
                        color: "#545A62",
                      }}
                    >
                      %100
                    </Box> */}
                  </Box>
                </ThemeProvider>
              </Box>
            </>
          )}
        </Box>

        {/* result */}
        <Box
          sx={{
            boxShadow: "0 20px 50px 0 rgba(40, 39, 129, 0.40)",
            borderRadius: "16px",
            padding: "24px",
            width: { xs: "100%", md: "40%" },
            bgcolor: "#282781",
          }}
        >
          <ProgressHeader
            label={duration ? getTenorLabel(Number(duration)) : ""}
            progress={progress}
          />
          <AmountCard
            text={t.calc.monthlyAmount}
            loading={loading}
            value={value}
          />
          <AmountCard
            text={t.calc.amount}
            loading={false}
            value={parseNumber(productPrice) - downPayment}
            color="#FADE17"
          />
        </Box>
      </Box>
    </Dialog>
  );
}
